import React, { useState, useMemo, useCallback, useEffect } from 'react';
import type { JSONContent } from '@tiptap/core';
import type { ElectronicSignatureBlock } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { createContentBlockStyles } from './styles/contentBlocks';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { InteractionMessage } from './InteractionMessage';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { RichTextContent } from './RichTextContent';

const descriptionResetStyles = createRichTextContentResetStyles(
  'esig-description-rich',
  '0.6em'
);

export interface ElectronicSignatureRendererProps {
  block: ElectronicSignatureBlock;
  onInteractionComplete?: () => void;
  onAcknowledged?: () => void;
  /**
   * NA-319 relaunch/session-nav restore. Electronic Signature has no
   * persisted per-learner response (no backend support at all), but when
   * present the checkbox mounts directly into its checked/locked state
   * instead of resetting to unchecked. Supplied by `CourseCanvas.tsx` from
   * either the learner's real, actually-checked state earlier in this
   * browser session, or — when the lesson containing this block is already
   * confirmed complete and this specific block instance requires
   * interaction — a guaranteed-checked fallback, since the lesson could not
   * have completed without this checkbox being checked.
   */
  initialAcknowledged?: boolean;
}

export function ElectronicSignatureRenderer({
  block,
  onInteractionComplete,
  onAcknowledged,
  initialAcknowledged,
}: ElectronicSignatureRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const richTextExtensions = useMemo(
    () => createRichTextRendererExtensions(),
    []
  );

  const [isAcknowledged, setIsAcknowledged] = useState(
    () => initialAcknowledged === true
  );

  // NA-319: `initialAcknowledged` can resolve asynchronously after this
  // block's first mount (same-session data is available immediately, but
  // the lesson-completion fallback depends on completion-status tracking
  // that may settle after first paint). The `!isAcknowledged` guard avoids
  // clobbering an in-progress or already-locked state.
  useEffect(() => {
    if (initialAcknowledged && !isAcknowledged) {
      setIsAcknowledged(true);
    }
  }, [initialAcknowledged, isAcknowledged]);

  const requireInteraction = block.requireInteraction ?? false;

  const isRichDescription = typeof block.description !== 'string';
  const descriptionHtml = useMemo(() => {
    if (typeof block.description === 'string') {
      return '';
    }
    return safeGenerateRichTextHTML(
      block.description as JSONContent,
      richTextExtensions
    );
  }, [block.description, richTextExtensions]);

  const hasDescription = useMemo(() => {
    if (typeof block.description === 'string') {
      return block.description.trim().length > 0;
    }
    const content = block.description as JSONContent;
    if (!content.content || content.content.length === 0) return false;
    return content.content.some(
      (node: JSONContent) => node.content && node.content.length > 0
    );
  }, [block.description]);

  const titleLevel = block.titleLevel ?? 'h1';
  // NA-227/NA-234: same three-tier content-block heading resolution as
  // Likert/Categorization/Single Choice/Map.
  const titleStyle = useMemo(() => {
    const typography = getContentBlockHeadingTypography(tokens, titleLevel, {
      isMobile,
      isSmallMobile,
    });
    return {
      fontSize: typography.size,
      fontWeight: typography.weight,
      lineHeight: typography.lineHeight,
      margin: 0,
    };
  }, [tokens, titleLevel, isMobile, isSmallMobile]);

  // NA-205/NA-227 convention: reading prose steps to 16px at ≤576.
  const proseFontSize = isSmallMobile
    ? tokens.typography.mobile.body.size
    : tokens.typography.body.size;
  const descriptionStyle = useMemo(
    () => ({ ...styles.esigDescription, fontSize: proseFontSize }),
    [styles.esigDescription, proseFontSize],
  );
  const checkboxLabelStyle = useMemo(
    () => ({ ...styles.esigCheckboxLabel, fontSize: proseFontSize }),
    [styles.esigCheckboxLabel, proseFontSize],
  );

  const handleAcknowledge = useCallback(() => {
    setIsAcknowledged(true);
    onAcknowledged?.();
    onInteractionComplete?.();
  }, [onAcknowledged, onInteractionComplete]);

  return (
    <div style={styles.esigContainer}>
      {block.title && (
        <div style={titleStyle} role="heading" aria-level={Number(titleLevel.replace('h', ''))}>
          {block.title}
        </div>
      )}

      {hasDescription && (
        isRichDescription ? (
          <>
            <style>{descriptionResetStyles}</style>
            <RichTextContent
              className="esig-description-rich"
              style={descriptionStyle}
              html={descriptionHtml}
            />
          </>
        ) : (
          <p style={descriptionStyle}>
            {block.description as string}
          </p>
        )
      )}

      <label style={{
        ...styles.esigCheckboxContainer,
        ...(isAcknowledged ? { cursor: 'default' } : {}),
      }}>
        <input
          type="checkbox"
          style={{
            ...styles.esigCheckboxInput,
            ...(isAcknowledged ? { cursor: 'default' } : {}),
          }}
          checked={isAcknowledged}
          disabled={isAcknowledged}
          onChange={handleAcknowledge}
          aria-label={block.checkboxLabel}
        />
        <span style={checkboxLabelStyle}>
          {block.checkboxLabel}
        </span>
      </label>

      <InteractionMessage
        variant="boxed"
        topOffset={50}
        requireInteraction={requireInteraction}
        isComplete={isAcknowledged}
        message={block.interactionMessage}
        fallbackMessage={t('electronic_signature.acknowledge_to_continue')}
      />
    </div>
  );
}

ElectronicSignatureRenderer.displayName = 'ElectronicSignatureRenderer';
